import { markerAt } from './replayInterpolate'
import type { ReplayBundle, ReplayParticipant } from './replayTypes'

/** Projects a geographic point to CSS pixels within the map viewport. */
export type ReplayProjection = (lat: number, lon: number) => [number, number]

export interface ReplayTrailSegment {
  /** True when the dog was barking over this run of the trail. */
  barking: boolean
  points: { x: number; y: number }[]
}

export interface ReplayTrailPath {
  participantId: string
  color: string
  /** Hunters draw dashed, dogs solid — the same distinction the mobile map makes. */
  dashed: boolean
  /**
   * Contiguous runs of the trail, split where the barking state changes.
   *
   * Neighbouring runs SHARE their boundary point, so stroking each one
   * separately still draws an unbroken line.
   */
  segments: ReplayTrailSegment[]
}

/** A point closing a run, carrying the barking state of the segment it ends. */
interface TrailVertex {
  x: number
  y: number
  /**
   * Whether the segment ENDING at this vertex is barking. The ending point
   * governs, for two reasons that agree with each other:
   *
   * - the mobile map engine evaluates its highlight filter on a span's END
   *   point, so attributing from the start would paint every red span one
   *   reporting interval later than mobile paints it for the same archive;
   * - the archive's bark count is a count over the reporting interval that ENDS
   *   at the fix, so the barking happened while the dog travelled the leg INTO
   *   this vertex, not the leg out of it.
   *
   * A lone barking fix therefore still renders as exactly one red tick — the leg
   * arriving at it — rather than reddening its neighbours.
   */
  barking: boolean
}

/**
 * Below this separation in CSS pixels a fix adds nothing a viewer can see, so it
 * is dropped from the path.
 *
 * This is the difference between a smooth replay and a stuttering one. A collar
 * reporting every few seconds while a dog holds position produces hundreds of
 * fixes inside one pixel; keeping them would mean hundreds of `lineTo` calls per
 * carrier per frame for no visible line. Filtering in screen space rather than
 * geographic space is what makes it correct at every zoom level — zoom in and the
 * same fixes separate and come back.
 */
const MIN_SCREEN_SEPARATION_PX = 1.5

/**
 * The trail each visible participant has drawn by `cursor`.
 *
 * Only fixes inside the participant's active window and at or before the cursor
 * are included, so a trail grows as playback advances and never shows where
 * someone will be. The final point is the interpolated position at the cursor,
 * so the line meets its marker instead of stopping at the last whole fix.
 */
export function trailPathsAt(
  bundle: ReplayBundle,
  cursor: number,
  hiddenParticipants: ReadonlySet<string>,
  project: ReplayProjection,
): ReplayTrailPath[] {
  const paths: ReplayTrailPath[] = []
  for (const participant of bundle.participants) {
    if (hiddenParticipants.has(participant.id)) continue
    // Hunters never carry barking data, and a dashed line with red runs in it
    // would read as a signal that does not exist.
    const barkAware = participant.role === 'dog'
    const vertices = trailVertices(participant, cursor, project, barkAware)
    if (vertices.length < 2) continue
    paths.push({
      participantId: participant.id,
      color: participant.color,
      dashed: participant.role === 'hunter',
      segments: toSegments(vertices),
    })
  }
  return paths
}

/**
 * Groups the vertices into runs of equal barking state.
 *
 * Iteration is over SEGMENTS, not vertices: segment `i` joins vertex `i` to
 * vertex `i + 1` and takes its state from vertex `i + 1` — the END of the leg,
 * which is where both the map engine's span attribution and the archive's
 * per-interval bark count put it. A run therefore always holds at least two
 * points, and consecutive runs share the vertex between them — the seam is drawn
 * by both, so stroking them separately leaves no gap. The first vertex only
 * opens the trail and governs no segment of its own.
 */
function toSegments(vertices: TrailVertex[]): ReplayTrailSegment[] {
  const segments: ReplayTrailSegment[] = []
  let current: ReplayTrailSegment | null = null

  for (let i = 0; i < vertices.length - 1; i++) {
    const { barking } = vertices[i + 1]
    const from = { x: vertices[i].x, y: vertices[i].y }
    const to = { x: vertices[i + 1].x, y: vertices[i + 1].y }
    if (current === null || current.barking !== barking) {
      current = { barking, points: [from, to] }
      segments.push(current)
    } else {
      current.points.push(to)
    }
  }

  return segments
}

function trailVertices(
  participant: ReplayParticipant,
  cursor: number,
  project: ReplayProjection,
  barkAware: boolean,
): TrailVertex[] {
  const upTo = Math.min(cursor, participant.windowEnd)
  const vertices: TrailVertex[] = []
  let last: TrailVertex | null = null

  for (const fix of participant.fixes) {
    if (fix.t < participant.windowStart) continue
    if (fix.t > upTo) break
    const barking = barkAware && fix.barking === true
    const [x, y] = project(fix.lat, fix.lon)
    // A fix that changes the barking state is kept whatever its separation: a
    // dog holding position and baying reports every fix inside one pixel, and
    // decimating those away would erase the red exactly where it matters most.
    // The pair the exemption keeps can be a small fraction of a pixel apart, and
    // strokes as a round-cap dot roughly a line-width across. That dot IS the
    // intended rendering of a single barking interval, not a degenerate run.
    const isTransition = last !== null && barking !== last.barking
    if (last !== null && !isTransition && distance(last, x, y) < MIN_SCREEN_SEPARATION_PX) {
      continue
    }
    const vertex: TrailVertex = { x, y, barking }
    vertices.push(vertex)
    last = vertex
  }

  // Join the trail to where the marker actually is. Without this the line lags
  // the marker by up to one reporting interval, which at 100× is a visible gap
  // the marker drags behind it.
  //
  // The head's barking state is the one `markerAt` resolved for the interpolated
  // position (`before.barking`), and because a vertex governs the segment ENDING
  // at it, that flag is what colours the trail's final leg.
  //
  // The barking badge above the marker is NOT this flag: it comes from a
  // trailing-window OR over the fixes just behind the cursor, so it deliberately
  // over-reports relative to per-leg colouring — the badge can be lit while the
  // final leg is drawn in the dog's own colour.
  const head = markerAt(participant, cursor)
  if (head !== null) {
    const [x, y] = project(head.lat, head.lon)
    if (last === null || distance(last, x, y) >= MIN_SCREEN_SEPARATION_PX) {
      vertices.push({ x, y, barking: barkAware && head.barking })
    }
  }

  return vertices
}

function distance(from: { x: number; y: number }, x: number, y: number): number {
  return Math.hypot(x - from.x, y - from.y)
}
